import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FiAlertTriangle, FiBookOpen, FiCalendar, FiCheckCircle, FiChevronDown, FiChevronUp, FiHash, FiRss, FiVolume2, FiX, FiZoomIn } from 'react-icons/fi'
import Card, { CardBody, CardHeader } from '../../components/ui/Card'
import Badge from '../../components/ui/Badge'
import type { BriefCard as BriefCardData } from '../../lib/dailyBriefApi'

const PANEL_LABELS = [
  { ko: '위험 인식', en: 'Hazard Recognition', color: 'amber' },
  { ko: '예방 행동 1', en: 'Prevention 1', color: 'emerald' },
  { ko: '예방 행동 2', en: 'Prevention 2', color: 'emerald' },
  { ko: '예방 행동 3', en: 'Prevention 3', color: 'emerald' },
] as const

interface Props {
  brief: BriefCardData
  onView?: () => void
}

export default function BriefCardView({ brief, onView }: Props) {
  const { t, i18n } = useTranslation()
  const c = brief.card
  const [ttsOpen, setTtsOpen] = useState(false)
  const [zoomOpen, setZoomOpen] = useState(false)
  const lang = (i18n.language || 'ko').startsWith('en') ? 'en' : 'ko'

  // ESC 로 zoom 닫기
  useEffect(() => {
    if (!zoomOpen) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setZoomOpen(false) }
    window.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [zoomOpen])

  // 이미지 저장 차단: 우클릭·드래그·선택 금지 (스크린샷은 OS 레벨이라 막을 수 없음)
  const noSaveProps = {
    onContextMenu: (e: React.MouseEvent) => e.preventDefault(),
    onDragStart: (e: React.DragEvent) => e.preventDefault(),
    draggable: false,
    style: {
      userSelect: 'none' as const,
      WebkitUserSelect: 'none' as const,
      WebkitTouchCallout: 'none' as const,
      pointerEvents: 'auto' as const,
    },
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              {brief.industry_label && <Badge>{brief.industry_label}</Badge>}
              {brief.accident_type && <Badge variant="warning">{brief.accident_type}</Badge>}
              {(brief.fatality_count ?? 0) > 0 && (
                <Badge variant="danger">
                  {t('dailyBrief.fatalityBadge', { defaultValue: '중대재해' })}
                </Badge>
              )}
            </div>
            <h2 className="text-lg font-bold text-ink-primary">
              {brief.title || c.summary.slice(0, 60)}
            </h2>
            {/* 날짜 3종 명확 구분: 사고발생일 · 출처(PDF) · 뉴스레터 발행일 */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-ink-faint mt-2">
              {brief.accident_date && (
                <span className="inline-flex items-center gap-1">
                  <span className="text-ink-secondary">🟥 사고 발생</span>
                  <span className="text-ink-primary font-medium">{brief.accident_date}</span>
                </span>
              )}
              <span className="inline-flex items-center gap-1">
                <span className="text-ink-secondary">📄 출처</span>
                <span className="text-ink-primary">행안부 일일상황보고서</span>
                {brief.source_post_id && <span className="text-ink-faint">#{brief.source_post_id}</span>}
              </span>
              <span className="inline-flex items-center gap-1">
                <FiCalendar size={11} />
                <span className="text-ink-secondary">📰 뉴스레터</span>
                <span className="text-ink-primary font-medium">{brief.published_date}</span>
              </span>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardBody>
        {/* 4컷 예방 만화 — 이미지 좌측(절반) + 우측에 위험인식/예방행동 그리드 */}
        {brief.image_paths && brief.image_paths.some(Boolean) && (
          <section className="mb-5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold text-ink-secondary uppercase tracking-wide">
                🎨 {t('dailyBrief.section.comic', { defaultValue: '예방 4컷 — 어떻게 막을 수 있었나' })}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-stretch">
              {/* 좌측: 이미지 (비율 유지, 좌측 정렬, 저장 차단, 클릭 시 확대) */}
              <button
                type="button"
                onClick={() => setZoomOpen(true)}
                className="relative w-full group rounded border border-surface-elevated overflow-hidden focus:outline-none focus:ring-2 focus:ring-brand-primary/40 self-start"
                aria-label={t('dailyBrief.section.comicZoom', { defaultValue: '4컷 만화 크게 보기' })}
              >
                <img
                  src={`/api/daily-brief/image/${brief.id}`}
                  alt={t('dailyBrief.section.comic', { defaultValue: '예방 4컷' })}
                  className="w-full aspect-square object-cover select-none pointer-events-none"
                  {...noSaveProps}
                />
                <span className="absolute top-2 right-2 inline-flex items-center justify-center w-7 h-7 rounded-full bg-black/40 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                  <FiZoomIn size={14} />
                </span>
              </button>
              {/* 우측: 위험 인식 + 예방 행동 3개 — 좌측 이미지 높이와 균등 분배 */}
              <div className="grid grid-rows-4 gap-2 h-full">
                <div className="px-3 py-2 rounded border-l-2 border-amber-500/60 bg-amber-500/5 flex flex-col justify-center">
                  <div className="text-[11px] font-bold text-amber-700 dark:text-amber-400 mb-1">
                    ① {lang === 'ko' ? '위험 인식' : 'Hazard Recognition'}
                  </div>
                  <div className="text-xs text-ink-secondary leading-snug">
                    {lang === 'ko'
                      ? '작업 시작 전 현장의 위험요인을 확인하고 점검표를 작성합니다.'
                      : 'Identify hazards before starting work, complete a checklist.'}
                  </div>
                </div>
                {(c.checkpoints || []).slice(0, 3).map((cp, i) => (
                  <div key={i} className="px-3 py-2 rounded border-l-2 border-emerald-500/60 bg-emerald-500/5 flex flex-col justify-center">
                    <div className="text-[11px] font-bold text-emerald-700 dark:text-emerald-400 mb-1">
                      {['②', '③', '④'][i]} {lang === 'ko' ? `예방 행동 ${i + 1}` : `Prevention ${i + 1}`}
                    </div>
                    <div className="text-xs text-ink-primary leading-snug">{cp}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* 사고개요 */}
        <section className="mb-4">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-ink-secondary uppercase tracking-wide mb-1">
            <FiAlertTriangle size={12} />
            {t('dailyBrief.section.summary', { defaultValue: '사고 개요' })}
          </div>
          <p className="text-sm text-ink-primary leading-relaxed">{c.summary}</p>
        </section>

        {/* 직접원인 */}
        <section className="mb-4">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-ink-secondary uppercase tracking-wide mb-1">
            <FiHash size={12} />
            {t('dailyBrief.section.cause', { defaultValue: '직접 원인' })}
          </div>
          <p className="text-sm text-ink-primary leading-relaxed">{c.direct_cause}</p>
        </section>

        {/* 유사사례 힌트 */}
        {c.similar_case_hint && (
          <section className="mb-4">
            <div className="text-xs font-semibold text-ink-secondary uppercase tracking-wide mb-1">
              {t('dailyBrief.section.pattern', { defaultValue: '유사 패턴' })}
            </div>
            <p className="text-sm text-ink-secondary italic leading-relaxed">{c.similar_case_hint}</p>
          </section>
        )}

        {/* 관련 법령 */}
        {c.laws && c.laws.length > 0 && (
          <section className="mb-4 p-3 rounded bg-surface-elevated/40 border border-surface-elevated">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-ink-secondary uppercase tracking-wide mb-2">
              <FiBookOpen size={12} />
              {t('dailyBrief.section.laws', { defaultValue: '관련 법령' })}
            </div>
            <ul className="space-y-1.5">
              {c.laws.map((law, i) => (
                <li key={i} className="text-sm">
                  <span className="font-medium text-ink-primary">
                    {law.law_name} {law.article}
                  </span>
                  <span className="text-ink-secondary"> — {law.summary}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* 체크포인트 3개 (핵심) */}
        <section className="mb-4">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-ink-secondary uppercase tracking-wide mb-2">
            <FiCheckCircle size={12} />
            {t('dailyBrief.section.checkpoints', { defaultValue: '오늘 할 일' })}
          </div>
          <ol className="space-y-2">
            {c.checkpoints.map((cp, i) => (
              <li key={i} className="flex gap-2 text-sm text-ink-primary leading-relaxed">
                <span className="flex-none w-5 h-5 rounded-full bg-brand-primary/15 text-brand-primary text-xs font-bold flex items-center justify-center mt-0.5">
                  {i + 1}
                </span>
                <span className="flex-1">{cp}</span>
              </li>
            ))}
          </ol>
        </section>

        {/* TTS 스크립트 (펼침) */}
        {c.tts_script && (
          <section className="mb-4">
            <button
              onClick={() => setTtsOpen(v => !v)}
              className="w-full flex items-center justify-between gap-2 p-2 rounded bg-surface-elevated/30 hover:bg-surface-elevated/50 transition-colors text-sm text-ink-secondary"
            >
              <span className="inline-flex items-center gap-1.5">
                <FiVolume2 size={14} />
                {t('dailyBrief.section.tts', { defaultValue: '90초 안전 브리핑 스크립트' })}
              </span>
              {ttsOpen ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
            </button>
            {ttsOpen && (
              <div className="mt-2 p-3 rounded bg-surface-elevated/20 border border-surface-elevated/50 text-sm text-ink-primary leading-relaxed whitespace-pre-wrap">
                {c.tts_script}
              </div>
            )}
          </section>
        )}

        {/* 이 사고와 직접 연결된 뉴스만 */}
        {brief.news_articles && brief.news_articles.length > 0 && (
          <section className="mb-4">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-ink-secondary uppercase tracking-wide mb-2">
              <FiRss size={12} />
              {t('dailyBrief.section.relatedNews', { defaultValue: '관련 보도' })}
              <span className="text-ink-faint font-normal">({brief.news_articles.length})</span>
            </div>
            <ul className="space-y-1.5">
              {brief.news_articles.slice(0, 5).map((n) => (
                <li key={n.id}>
                  <a
                    href={n.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block px-2.5 py-1.5 rounded border border-surface-elevated/50 hover:border-cyan-500/40 hover:bg-cyan-500/5 transition-colors text-xs"
                  >
                    <div className="font-medium text-ink-primary line-clamp-2">{n.title}</div>
                    {n.publisher && (
                      <div className="text-[10px] text-ink-faint mt-0.5">{n.publisher}</div>
                    )}
                  </a>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* 추모 노트 — 사망 사고일 때만 표시 */}
        {c.memorial_note && (brief.fatality_count ?? 0) > 0 && (
          <div className="mt-4 p-3 rounded bg-surface-elevated/30 border-l-2 border-ink-faint/40 text-xs text-ink-faint italic">
            {c.memorial_note}
          </div>
        )}

        {/* 출처표시 (KOGL 의무) */}
        {brief.source_attribution && (
          <div className="mt-4 pt-3 border-t border-surface-elevated/60 text-[11px] text-ink-faint leading-relaxed">
            {brief.source_attribution}
          </div>
        )}

        {/* AI 면책 */}
        <div className="mt-2 text-[11px] text-ink-faint">
          {t('dailyBrief.disclaimer', {
            defaultValue: '본 카드는 AI가 KOSHA 공개 사고사례를 요약·가공한 교육용 자료입니다. 실제 적용 전 출처 원문을 확인하세요.',
          })}
        </div>
      </CardBody>

      {/* 4컷 만화 확대 모달 — backdrop 클릭/ESC 로 닫힘, 이미지 저장 차단 */}
      {zoomOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setZoomOpen(false)}
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            className="absolute top-4 right-4 inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white"
            onClick={(e) => { e.stopPropagation(); setZoomOpen(false) }}
            aria-label={t('common.close', { defaultValue: '닫기' })}
          >
            <FiX size={18} />
          </button>
          <img
            src={`/api/daily-brief/image/${brief.id}`}
            alt={t('dailyBrief.section.comic', { defaultValue: '예방 4컷' })}
            className="max-w-[92vw] max-h-[88vh] object-contain select-none cursor-default"
            onClick={(e) => e.stopPropagation()}
            {...noSaveProps}
          />
        </div>
      )}
    </Card>
  )
}
